import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-current [&_svg]:opacity-100",
  {
    variants: {
      variant: {
        // AA verified: white on #156e54 = 6.19:1
        default:
          "bg-primary text-primary-foreground hover:bg-primary active:bg-primary focus:bg-primary focus-visible:bg-primary disabled:bg-muted disabled:text-muted-foreground disabled:border disabled:border-border",
        // AA verified: white on #ca2121 = 5.61:1
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive active:bg-destructive focus-visible:bg-destructive disabled:bg-muted disabled:text-muted-foreground disabled:border disabled:border-border",
        // AA verified: #156e54 on cream = 5.05:1 (idle) / white on #156e54 = 6.19:1 (hover)
        outline:
          "border border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground active:bg-primary focus-visible:bg-primary focus-visible:text-primary-foreground disabled:bg-muted disabled:text-muted-foreground disabled:border-border",
        // AA verified: near-black on #eee7d8 ≈ 14:1
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary active:bg-secondary focus-visible:bg-secondary disabled:bg-muted disabled:text-muted-foreground",
        // AA verified: dark lavender on soft lavender ≈ 9:1
        ghost:
          "text-foreground hover:bg-accent hover:text-accent-foreground active:bg-accent focus-visible:bg-accent focus-visible:text-accent-foreground disabled:bg-transparent disabled:text-muted-foreground",
        // AA verified: #156e54 on cream = 5.05:1
        link:
          "text-primary underline-offset-4 hover:text-primary hover:underline active:text-primary focus-visible:text-primary visited:text-primary disabled:text-muted-foreground disabled:no-underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
